<template>
  <div class="content">
    <div class="content-title">
      <p class="title-text">Custom Domains</p>
      <p class="subtitle">
        Manage your domain names that are powered by ArtistHub.
      </p>
    </div>
    <div class="add-custom-domain-card">
      <div class="card-top">
        <span class="card-title">Add Custom Domain</span>
        <p class="card-text">
          Add your own custom domain to use for your artist links.<br />
          <span class="bold">Important:</span> you need to set the
          <span class="bold">CNAME</span> on your domain to
          <span class="bold">vibe.to</span> before you can add it.
        </p>
      </div>
      <div class="card-content">
        <div class="custom-domain-input">
          <label>
            <span class="label">Custom domain</span>
            <input
              v-model="newCustomDomain"
              type="text"
              placeholder="Please enter"
            />
          </label>
        </div>
        <button
          @click="addCustomDomain"
          class="button-default"
          :disabled="customDomainLoading || !newCustomDomain"
        >
          <span>Add</span>
        </button>
      </div>
    </div>
    <div class="edit-custom-domains">
      <div
        v-for="custom_domain in custom_domains"
        :key="custom_domain.id"
        class="custom-domain-card"
      >
        <div class="header">
          <div class="title">
            <span class="domain">
              {{ custom_domain.domain }}
            </span>
            <span class="subtitle">{{
              customDomainSubtitle(custom_domain)
            }}</span>
          </div>
          <div class="actions">
            <button
              v-if="!custom_domain.editing"
              @click="toggleCustomDomainEdit(custom_domain, true)"
              class="button-secondary big"
            >
              <span>Edit</span>
            </button>
            <button
              v-if="custom_domain.editing"
              @click="toggleCustomDomainEdit(custom_domain, false)"
              class="button-default big"
            >
              <span>Save</span>
            </button>
            <button
              v-if="custom_domain.editing"
              class="button-text danger"
              @click="removeCustomDomain(custom_domain)"
            >
              <span>[DANGER] Delete Domain</span>
            </button>
          </div>
        </div>
        <div v-if="custom_domain.editing" class="checkboxes">
          <label
            class="checkbox bounce"
            v-for="artist in allArtists"
            :key="artist.id"
          >
            <input
              type="checkbox"
              v-model="domains[custom_domain.id]"
              :value="artist.id"
              class="artist-checkbox"
              @change="cbChange($event, custom_domain.id, artist.id)"
            />
            <CheckIcon />
            <span>{{ artist.name }}</span>
          </label>
        </div>
      </div>
    </div>

    <SnackBar />
  </div>
</template>

<script>
import CheckIcon from "../../../../public/img/icons/check.svg";
import {
  M_ADD_CUSTOM_DOMAIN,
  M_DELETE_CUSTOM_DOMAIN,
  M_DELETE_CUSTOM_DOMAIN_ARTIST,
  M_INSERT_CUSTOM_DOMAIN_ARTISTS,
  Q_CUSTOM_DOMAINS,
  S_ARTISTS
} from "@/graphql";
import Swal from "sweetalert2";
import "@/assets/scss/custom/vendors-extensions/_sweetalert.scss";
import "@/assets/scss/custom/vendors-extensions/_braintree.scss";
import SnackBar from "@/components/Custom/SnackBar";
export default {
  name: "UserSettings",
  components: {
    CheckIcon,
    SnackBar
  },
  data() {
    const user = this.$store.getters.getUser;
    return {
      subscriptionUserId: user.master_account_id || user.id,
      newCustomDomain: null,
      customDomainLoading: false,
      custom_domains: [],
      invoiceHistory: [],
      domains: {},
      allArtists: []
    };
  },
  apollo: {
    $subscribe: {
      allArtists: {
        query: S_ARTISTS,
        variables() {
          return { user_id: this.subscriptionUserId };
        },
        result({ data }) {
          this.allArtists = data.artists;
        }
      }
    },
    custom_domains: {
      query: Q_CUSTOM_DOMAINS,
      variables() {
        return {
          user_id: this.user.master_account_id || this.user.id
        };
      },
      result({ data }) {
        console.log("domains subscribe", data);
        data.custom_domains.forEach(custom_domain => {
          this.$set(this.domains, custom_domain.id, []);
          if (
            custom_domain.custom_domain_artists &&
            custom_domain.custom_domain_artists.length
          ) {
            custom_domain.custom_domain_artists.forEach(x => {
              this.domains[custom_domain.id].push(x.artist_id);
            });
          }
        });
        this.custom_domains = data.custom_domains;
      }
    }
  },
  filters: {
    date(val) {
      return new Date(val).toLocaleDateString();
    },
    price(val) {
      if (val) {
        return val.toFixed(2);
      } else {
        return 0;
      }
    },
    plan(val, subscriptionActive) {
      if (val.plan == "family") {
        return "Pro (Fam)";
      }
      if (val.plan == "agency") {
        return "Pro (Agency)";
      }
      if (!subscriptionActive) {
        return "Suspended";
      } else if (val.plan && val.plan.includes("lite")) {
        return "Lite";
      } else if (!val.plan) {
        return "";
      } else {
        return "Pro";
      }
    }
  },
  computed: {
    artist() {
      return this.$store.getters.getArtist;
    },
    user() {
      return this.$store.getters.getUser;
    },
    billingStart: function() {
      if (this.user.first_billing_date) {
        return this.user.first_billing_date;
      }
      let months = 1;
      if (this.user.coupon === "ARISTAKE") {
        months = 6;
      } else if (["JASSYANG", "JESSYANG"].includes(this.user.coupon)) {
        months = 2;
      }
      const billing_start = new Date();
      billing_start.setMonth(billing_start.getMonth() + months);
      return billing_start.toLocaleDateString();
    },
    billingStartDays: function() {
      if (!this.user.first_billing_date) {
        return -1;
      }
      let today = new Date().getTime();
      let firstBillingDate = new Date(this.user.first_billing_date).getTime();
      return Math.floor((firstBillingDate - today) / (1000 * 3600 * 24));
    },
    monthlyCharge: function() {
      if (this.user.plan)
        switch (this.user.plan) {
          case "agency":
            return 179;
          case "base_14":
            return 14;
          case "base_ata":
            return 9;
          default:
            return 17;
        }

      switch (this.user.coupon) {
        case "AGENCY":
          return 179;
        case "ARISTAKE":
          return 14;
        default:
          return 17;
      }
    },
    isUnMigratedUser() {
      if (this.user && this.allArtists) {
        return (
          this.allArtists.filter(x => x.braintree_subscription_id === null)
            .length !== 0
        );
      } else {
        return false;
      }
    }
  },
  methods: {
    async manageArtist(artist) {
      await this.$store.dispatch(
        "setArtist",
        this.allArtists.filter(x => x.id === artist.id)[0]
      );
      await this.$router.push({ name: "ArtistSettings" });
    },
    onError(error) {
      // let message = error.message;
      console.log("onerror", error);
      // Whoops, an error has occured while trying to get the nonce
    },
    async addCustomDomain() {
      try {
        this.customDomainLoading = true;
        await this.$apollo.mutate({
          mutation: M_ADD_CUSTOM_DOMAIN,
          client: "localClient",
          variables: {
            domain: this.newCustomDomain
          }
        });
        await this.$apollo.queries.custom_domains.refetch();
        this.newCustomDomain = null;
        await this.$store.dispatch("snack", {
          text: "Success! Custom domain added."
        });
      } catch (err) {
        let errorMessage = err.message.replace("GraphQL error: ", "");
        if (errorMessage.includes("queryCname ENOTFOUND")) {
          errorMessage =
            "It looks like this domain doesn’t have a CNAME record pointing to vibe.to";
        } else if (errorMessage.includes("Uniqueness violation")) {
          errorMessage =
            "It looks like this domain is already added by another user";
        }
        await this.$store.dispatch("snack", {
          type: "error",
          text: `Oops! ${errorMessage}`
        });
      } finally {
        this.customDomainLoading = false;
      }
    },
    toggleCustomDomainEdit(custom_domain, editing) {
      this.$set(custom_domain, "editing", editing);
    },
    customDomainSubtitle(custom_domain) {
      if (custom_domain.editing) {
        return "Select which artists can use this domain:";
      } else if (
        custom_domain.custom_domain_artists &&
        custom_domain.custom_domain_artists.length
      ) {
        const artists = custom_domain.custom_domain_artists
          .flatMap(x => {
            const name = this.allArtists.find(
              artist => artist.id === x.artist_id
            )?.name;
            return name ? [name] : [];
          })
          .sort();
        return `Assigned to ${artists.join(", ")}`;
      } else {
        return "Not assigned to any artist";
      }
    },
    async removeCustomDomain(custom_domain) {
      try {
        const confirmRemove = await Swal.fire({
          title: `Remove this custom domain?`,
          text: "It will remove all artist links using this domain.",
          type: "warning",
          showCloseButton: true,
          showCancelButton: true,
          confirmButtonText: "Yes, remove domain and links",
          cancelButtonText: "Cancel",
          confirmButtonClass: "button-danger",
          cancelButtonClass: "button-default",
          buttonsStyling: false
        });
        console.log(confirmRemove);
        if (confirmRemove.value) {
          await this.$apollo.mutate({
            mutation: M_DELETE_CUSTOM_DOMAIN,
            client: "localClient",
            variables: {
              domain_id: custom_domain.id
            }
          });
          this.custom_domains = this.custom_domains.filter(
            x => x.id !== custom_domain.id
          );
        }
      } catch (err) {
        await this.$store.dispatch("snack", {
          type: "error",
          text: `Oops! ${err.message.replace("GraphQL error: ", "")}`
        });
      }
    },
    async cbChange($event, domain_id, artist_id) {
      console.log($event, domain_id, artist_id);
      if (this.domains[domain_id].includes(artist_id)) {
        // enable custom domain for artist
        console.log("enable custom domain for artist");
        await this.$apollo.mutate({
          mutation: M_INSERT_CUSTOM_DOMAIN_ARTISTS,
          variables: {
            artist_id: artist_id,
            custom_domain_id: domain_id
          }
        });
      } else {
        // disable custom domain for artist
        console.log("disable custom domain for artist");
        await this.$apollo.mutate({
          mutation: M_DELETE_CUSTOM_DOMAIN_ARTIST,
          variables: {
            custom_domain_id: domain_id,
            artist_id: artist_id
          }
        });
      }
      await this.$apollo.queries.custom_domains.refetch();
    }
  }
};
</script>
<style lang="scss" scoped>
//@import "../../../assets/scss/custom/components/button/buttons";
//@import "../../../assets/scss/custom/globals/colors";
//@import "../../../assets/scss/custom/globals/fonts";
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99;
  .verify-modal {
    padding: 60px 184px;
    z-index: 98;
    background: linear-gradient(
      241.25deg,
      rgba(41, 39, 85, 0.45) 4.4%,
      rgba(41, 39, 84, 0.85) 61.77%,
      rgba(27, 24, 66, 0.45) 119.94%
    );
    //box-shadow: 0px 51px 69px rgba(23, 18, 43, 0.585739);
    backdrop-filter: blur(23px);
    /* Note: backdrop-filter has minimal browser support */
    border-radius: 10px;
    .modal-close {
      cursor: pointer;
      position: absolute;
      top: 18px;
      right: 18px;
      display: flex;
      flex-direction: column;
      &:hover {
        svg {
          path {
            fill: white;
          }
        }
        .close-text {
          color: white;
        }
      }
      svg {
        width: 24px;
        path {
          fill: #8786ab;
        }
      }
      .close-text {
        font-weight: 600;
        font-size: 8px;
        line-height: 10px;
        text-align: center;
        text-transform: uppercase;
        /* Text/type2 */
        color: $text-type-2;
      }
    }
    .modal-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      .lets-start-heading {
        margin-top: 30px;
        font-weight: bold;
        font-size: 30px;
        line-height: 37px;
        /* White */
        color: white;
        mix-blend-mode: normal;
        opacity: 0.9;
      }
      .description {
        margin-top: 30px;
        /* 16/Regular */
        font-size: 16px;
        line-height: 24px;
        text-align: center;
        /* Text/type0 */
        color: $text-type-0;
      }
      .user-email-text {
        margin-top: 6px;
        /* 16/Regular */
        font-size: 16px;
        line-height: 24px;
        text-align: center;
        /* White */
        color: white;
      }
      .inputs {
        margin-top: 36px;
      }
      .continue-button {
        margin-top: 36px;
        padding: 9px 56px;
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
      }
      .no-code-text {
        margin-top: 30px;
        /* 16/Regular */
        font-size: 16px;
        line-height: 24px;
        /* Text/type0 */
        color: $text-type-0;
      }
      .button-text {
        margin-top: 10px;
      }
    }
  }
  .background-overlay {
    z-index: 97;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    cursor: unset;
    /* Purple-dark */
    background: $purple-dark;
    opacity: 0.9;
    webkit-backdrop-filter: blur(10px) !important;
    backdrop-filter: blur(10px) !important;
  }
}
.content {
  .content-title {
    .title-text {
      margin: 0;
      padding-bottom: 6px;
      font-weight: bold;
      font-size: 30px;
      line-height: 37px;
      color: white;
      mix-blend-mode: normal;
      opacity: 0.9;
    }
    .subtitle {
      margin: 0;
      padding-bottom: 36px;
      /* 16/Regular */
      font-size: 16px;
      line-height: 24px;
      /* Text/type2 */
      color: $text-type-2;
    }
  }
}
.edit-profile-card {
  padding: 24px;
  /* BG/type2 */
  background: $bg-type-2;
  /* Shadow */
  box-shadow: 0px 50px 40px rgba(23, 18, 43, 0.3);
  border-radius: 10px;
  .card-top {
    .card-title {
      /* 18/SemiBold */
      font-weight: 600;
      font-size: 18px;
      line-height: 30px;
      /* White */
      color: white;
      mix-blend-mode: normal;
      opacity: 0.9;
    }
    .card-text {
      margin: 0;
      font-size: 14px;
      line-height: 24px;
      /* Text/type0 */
      color: $text-type-0;
      mix-blend-mode: normal;
      opacity: 0.9;
    }
  }
  .card-content {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    .inputs {
      margin-top: 24px;
      width: 100%;
      //display: flex;
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr 1fr;
      gap: 18px 32px;
      grid-template-areas:
        "firstname lastname"
        "email email";

      .first-name {
        grid-area: firstname;
      }

      .last-name {
        grid-area: lastname;
      }

      .email-input {
        grid-area: email;
      }

      label {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        .label {
          margin-left: 11px;
          width: 100%;
          /* 12/Bold */
          font-weight: bold;
          font-size: 12px;
          line-height: 15px;
          /* Text/type1 */
          color: $text-type-1;
        }
        input {
          margin-top: 6px;
          padding: 13px 11px;
          //width: 100%;
          flex: 1;
          /* Purple-dark */
          background: $purple-dark;
          /* Purple-stroke */
          border: 1px solid $purple-stroke;
          box-sizing: border-box;
          border-radius: 5px;
          outline: none;
          //border: none;
          color: $text-type-0;
          /* 14/Regular */
          font-size: 14px;
          line-height: 17px;
          &:focus {
            outline: none;
            //border: 1px solid $purple;
          }
          &::placeholder {
            color: $text-type-2;
            /* 14/Regular */
            font-size: 14px;
            line-height: 17px;
          }
        }
      }
    }
    button {
      margin-top: 18px;
      padding: 9px 30px;
      &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }
    }
  }
}
.update-password-card {
  margin-top: 36px;
  padding: 24px;
  /* BG/type2 */
  background: $bg-type-2;
  /* Shadow */
  box-shadow: 0px 50px 40px rgba(23, 18, 43, 0.3);
  border-radius: 10px;
  .card-top {
    .card-title {
      /* 18/SemiBold */
      font-weight: 600;
      font-size: 18px;
      line-height: 30px;
      /* White */
      color: white;
      mix-blend-mode: normal;
      opacity: 0.9;
    }
    .card-text {
      margin: 0;
      font-size: 14px;
      line-height: 24px;
      /* Text/type0 */
      color: $text-type-0;
      mix-blend-mode: normal;
      opacity: 0.9;
    }
  }
  .card-content {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    .password-inputs {
      margin-top: 24px;
      width: 100%;
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      grid-column-gap: 32px;
      .current,
      .new,
      .confirm {
        label {
          width: 100%;
          display: flex;
          flex-wrap: wrap;
          align-items: baseline;
          .label {
            margin-left: 11px;
            width: 100%;
            /* 12/Bold */
            font-weight: bold;
            font-size: 12px;
            line-height: 15px;
            /* Text/type1 */
            color: #8786ab;
          }
          input {
            margin-top: 6px;
            padding: 13px 11px;
            //width: 100%;
            flex: 1;
            /* Purple-dark */
            background: $purple-dark;
            /* Purple-stroke */
            border: 1px solid $purple-stroke;
            box-sizing: border-box;
            border-radius: 5px;
            outline: none;
            //border: none;
            color: $text-type-0;
            /* 14/Regular */
            font-size: 14px;
            line-height: 17px;
            &:focus {
              outline: none;
              //border: 1px solid $purple;
            }
            &::placeholder {
              color: $text-type-2;
              /* 14/Regular */
              font-size: 14px;
              line-height: 17px;
            }
          }
        }
      }
    }
    button {
      margin-top: 18px;
      padding: 9px 30px;
      &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }
    }
  }
}
.billing-card {
  display: grid;
  grid-gap: 24px;
  margin-top: 36px;
  padding: 24px;
  /* BG/type2 */
  background: $bg-type-2;
  /* Shadow */
  box-shadow: 0px 50px 40px rgba(23, 18, 43, 0.3);
  border-radius: 10px;
  .card-top {
    .card-title {
      /* 18/SemiBold */
      font-weight: 600;
      font-size: 18px;
      line-height: 30px;
      /* White */
      color: white;
      mix-blend-mode: normal;
      opacity: 0.9;
    }
    .card-text {
      margin: 0;
      font-size: 14px;
      line-height: 24px;
      /* Text/type0 */
      color: $text-type-0;
      mix-blend-mode: normal;
      opacity: 0.9;
      .bold {
        font-weight: 600;
        //color: white;
      }
    }
  }
  .card-content {
    display: grid;
    grid-gap: 24px;
    .subscriptions {
      display: grid;
      grid-gap: 12px;
      .subscription-title {
        font-size: 14px;
        line-height: 30px;
        /* Text/type0 */
        color: $text-type-0;
        mix-blend-mode: normal;
        opacity: 0.9;
      }
      .plans {
        width: 100%;
        display: grid;
        //grid-auto-flow: column;
        //grid-gap: 24px;
        grid-gap: 24px 30px;
        grid-template-columns: repeat(auto-fill, minmax(495px, auto));
        .plan {
          width: 100%;
          display: flex;
          gap: 12px;
          padding: 24px;
          /* BG/type1 */
          background: $bg-type-1;
          border-radius: 10px;
          &.active {
            border: 1px solid $green;
            box-sizing: border-box;
          }
          .plan-icon {
            display: flex;
            justify-content: center;
            align-items: center;
            img {
              border-radius: 50%;
              width: 64px;
              height: 64px;
              object-fit: contain;
            }
          }
          .plan-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 6px;
            .plan-name {
              display: flex;
              align-items: center;
              gap: 12px;
              .icon {
                width: 30px;
                height: 30px;
                border-radius: 50%;
                background: $green;
                display: flex;
                justify-content: center;
                align-items: center;
                svg {
                  width: 28px;
                  path {
                    fill: white;
                  }
                }
              }
              span {
                /* 18/SemiBold */
                font-weight: 600;
                font-size: 18px;
                line-height: 30px;
                /* White */
                color: white;
                mix-blend-mode: normal;
                opacity: 0.9;
              }
            }
            .price-info {
              font-size: 18px;
              line-height: 24px;
              /* Text/type0 */
              color: $text-type-0;
              mix-blend-mode: normal;
              opacity: 0.9;
            }
          }
          .plan-actions {
            display: flex;
            justify-content: center;
            align-items: center;
            .start {
              width: 136px;
            }
            .cancel {
              width: 117px;
            }
          }
        }
      }
    }
    .invoice-history {
      display: grid;
      grid-gap: 12px;
      .invoices-title {
        font-size: 14px;
        line-height: 30px;
        /* Text/type0 */
        color: $text-type-0;
        mix-blend-mode: normal;
        opacity: 0.9;
      }
      .invoices {
        display: grid;
        grid-gap: 12px;
        .invoice {
          width: 100%;
          background: $bg-type-1;
          border-radius: 10px;
          display: flex;
          flex-wrap: wrap;
          gap: 18px;
          padding: 12px;
          .invoice-collapsed {
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            gap: 18px;
            &:hover {
              cursor: pointer;
            }
            .invoice-icon {
              width: 32px;
              display: flex;
              justify-content: center;
              align-items: center;
              //padding: 6px;
            }
            .payment-method {
              flex: 1;
              display: flex;
              flex-direction: column;
              .method-info {
                /* 16/SemiBold */
                font-weight: 600;
                font-size: 16px;
                line-height: 24px;
                /* White */
                color: white;
                mix-blend-mode: normal;
                opacity: 0.9;
              }
              .invoice-subtitle {
                /* 12/Regular */
                font-size: 12px;
                line-height: 24px;
                /* Text/type0 */
                color: $text-type-0;
                mix-blend-mode: normal;
                opacity: 0.9;
              }
            }
            .payment-details {
              display: flex;
              flex-direction: column;
              text-align: right;
              .total {
                /* 16/SemiBold */
                font-weight: 600;
                font-size: 16px;
                line-height: 24px;
                /* White */
                color: white;
                mix-blend-mode: normal;
                opacity: 0.9;
              }
              .invoice-subtitle {
                /* 12/Regular */
                font-size: 12px;
                line-height: 24px;
                /* Text/type0 */
                color: $text-type-0;
                mix-blend-mode: normal;
                opacity: 0.9;
              }
            }
          }
          .invoice-expanded {
            width: 100%;
            display: flex;
            flex-direction: column;
            border-top: 1px solid $text-type-2;
            padding-top: 18px;
            .seller {
              display: flex;
              flex-direction: column;
              margin-bottom: 32px;
              .seller-name {
                /* 16/SemiBold */
                font-weight: 600;
                font-size: 16px;
                line-height: 24px;
                /* White */
                color: white;
                mix-blend-mode: normal;
                opacity: 0.9;
              }
              .seller-address {
                /* 12/Regular */
                font-size: 12px;
                line-height: 24px;
                /* Text/type0 */
                color: $text-type-0;
                mix-blend-mode: normal;
                opacity: 0.9;
              }
            }
            .expanded-title {
              font-weight: 600;
              font-size: 18px;
              line-height: 30px;
              color: white;
              mix-blend-mode: normal;
              opacity: 0.9;
            }
            .expanded-subtitle {
              font-weight: 600;
              font-size: 16px;
              line-height: 24px;
              /* White */
              color: white;
              mix-blend-mode: normal;
              opacity: 0.9;
            }
            .expanded-id {
              font-size: 16px;
              line-height: 24px;
              /* White */
              color: white;
              mix-blend-mode: normal;
              opacity: 0.9;
            }
            .for-details {
              margin-top: 32px;
              .for-details-header {
                display: flex;
                justify-content: space-between;
                margin-bottom: 12px;
                .detail-title {
                  color: white;
                }
              }
              .for-details-content {
                display: flex;
                justify-content: space-between;
                .for-email {
                  /* 16/SemiBold */
                  font-weight: 600;
                  font-size: 16px;
                  line-height: 24px;
                  /* White */
                  color: white;
                  mix-blend-mode: normal;
                  opacity: 0.9;
                }
                .for-date {
                  /* 16/Regular */
                  font-size: 16px;
                  line-height: 24px;
                  /* Text/type0 */
                  color: $text-type-0;
                  mix-blend-mode: normal;
                  opacity: 0.9;
                }
              }
            }
            .items-table {
              width: 100%;
              border-collapse: separate;
              border-spacing: 0 6px;
              margin-top: 6px;
              thead {
                tr {
                  th {
                    padding: 18px 18px 6px 0;
                    /* 12/SemiBold */
                    font-weight: 600;
                    font-size: 12px;
                    line-height: 15px;
                    /* Text/type0 */
                    color: $text-type-0;
                    /* &:first-child {
                      padding-left: 18px;
                    } */
                    .th-content {
                      display: flex;
                      align-items: center;
                      &.right {
                        justify-content: flex-end;
                      }
                    }
                  }
                }
              }
              tbody {
                tr {
                  /* BG/type1 */
                  background: $bg-type-1;
                  .email {
                    border-radius: 10px 0 0 10px;
                    //padding-left: 18px;
                    span {
                      /* 14/SemiBold */
                      font-size: 14px;
                      line-height: 28px;
                      /* White */
                      color: white;
                      mix-blend-mode: normal;
                      opacity: 0.9;
                    }
                  }
                  .role {
                    text-align: right;
                  }
                  td {
                    //padding: 18px;
                    //border: solid 1px transparent;
                    margin-bottom: 6px;
                    /* 14/Regular */
                    font-weight: normal;
                    font-size: 14px;
                    line-height: 28px;
                    /* White */
                    color: white;
                    mix-blend-mode: normal;
                    opacity: 0.9;
                  }
                }
              }
            }
            .download-invoice {
              color: #9c27b0;
              font-size: 0.9rem;
              margin: 1rem 0;
              &:hover {
                text-decoration: underline;
                cursor: pointer;
              }
            }
          }
        }
      }
    }
    .braintree-billing {
      display: flex;
      justify-content: center;
      flex-wrap: wrap;
      .payment {
        width: 100%;
        .button-default {
          //margin-top: -44px;
          padding: 9px 30px;
          float: right;
        }
      }
    }
  }
}
.custom-domains-title {
  margin-top: 48px;
  margin-bottom: 24px;
  /* 24/Bold */
  font-weight: bold;
  font-size: 24px;
  line-height: 29px;
  /* White */
  color: white;
  mix-blend-mode: normal;
  opacity: 0.9;
}
.add-custom-domain-card {
  padding: 24px;
  /* BG/type2 */
  background: $bg-type-2;
  /* Shadow */
  box-shadow: 0px 50px 40px rgba(23, 18, 43, 0.3);
  border-radius: 10px;
  .card-top {
    .card-title {
      /* 18/SemiBold */
      font-weight: 600;
      font-size: 18px;
      line-height: 30px;
      /* White */
      color: white;
      mix-blend-mode: normal;
      opacity: 0.9;
    }
    .card-text {
      margin: 0;
      font-size: 14px;
      line-height: 24px;
      /* Text/type0 */
      color: $text-type-0;
      mix-blend-mode: normal;
      opacity: 0.9;
      .bold {
        font-weight: 600;
        color: white;
      }
    }
  }
  .card-content {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    .custom-domain-input {
      margin-top: 24px;
      width: 100%;
      display: flex;
      label {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        .label {
          margin-left: 11px;
          width: 100%;
          /* 12/Bold */
          font-weight: bold;
          font-size: 12px;
          line-height: 15px;
          /* Text/type1 */
          color: #8786ab;
        }
        input {
          margin-top: 6px;
          padding: 13px 11px;
          //width: 100%;
          flex: 1;
          /* Purple-dark */
          background: $purple-dark;
          /* Purple-stroke */
          border: 1px solid $purple-stroke;
          box-sizing: border-box;
          border-radius: 5px;
          outline: none;
          //border: none;
          color: $text-type-0;
          /* 14/Regular */
          font-size: 14px;
          line-height: 17px;
          &:focus {
            outline: none;
            //border: 1px solid $purple;
          }
          &::placeholder {
            color: $text-type-2;
            /* 14/Regular */
            font-size: 14px;
            line-height: 17px;
          }
        }
      }
    }
    button {
      margin-top: 18px;
      padding: 9px 30px;
      &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }
    }
  }
}
.edit-custom-domains {
  margin-top: 24px;
  display: grid;
  //grid-template-columns: repeat(auto-fill, minmax(495px, auto));
  grid-gap: 24px;
  .custom-domain-card {
    padding: 24px;
    /* BG/type2 */
    background: $bg-type-2;
    /* Shadow */
    box-shadow: 0px 50px 40px rgba(23, 18, 43, 0.3);
    border-radius: 10px;
    .header {
      display: flex;
      justify-content: space-between;
      //margin-bottom: 6px;
      .title {
        .domain {
          /* 18/SemiBold */
          font-weight: 600;
          font-size: 18px;
          line-height: 30px;
          /* or 144% */
          display: flex;
          align-items: center;
          /* White */
          color: white;
          mix-blend-mode: normal;
          opacity: 0.9;
        }
        .subtitle {
          font-size: 14px;
          line-height: 24px;
          display: flex;
          align-items: center;
          /* Text/type0 */
          color: $text-type-0;
          mix-blend-mode: normal;
          opacity: 0.9;
        }
      }
      .actions {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 12px;
        button {
          min-width: 100px;
        }
      }
      //.remove {
      //  button {
      //    //margin-top: 18px;
      //    padding: 9px 15px;
      //    &:disabled {
      //      cursor: not-allowed;
      //      opacity: 0.5;
      //    }
      //  }
      //}
    }
    .checkboxes {
      padding-top: 6px;
      .checkbox {
        position: relative;
        margin-top: 18px;
        display: flex;
        align-items: center;
        span {
          margin-left: 12px;
          font-size: 14px;
          line-height: 17px;
          /* White */
          color: $text-type-0;
          mix-blend-mode: normal;
          opacity: 0.9;
        }
        input,
        svg {
          width: 24px;
          height: 24px;
          display: block;
        }
        input {
          -webkit-appearance: none;
          appearance: none;
          -moz-appearance: none;
          position: relative;
          outline: none;
          background: $text-type-0;
          border: none;
          margin: 0;
          padding: 0;
          cursor: pointer;
          border-radius: 5px;
          transition: box-shadow 0.3s;
          &:checked {
            background: $bg-type-1;
            & ~ svg {
              path {
                fill: white;
              }
            }
            & ~ span {
              color: white;
            }
          }
        }
        svg {
          pointer-events: none;
          fill: none;
          stroke-width: 2px;
          stroke-linecap: round;
          stroke-linejoin: round;
          position: absolute;
          top: 0;
          left: 0;
          width: 24px;
          height: 24px;
          path {
            fill: transparent;
          }
        }
      }
      //.checkbox {
      //  margin-top: 18px;
      //  display: flex;
      //  align-items: center;
      //  input {
      //    appearance: none;
      //    width: 24px;
      //    height: 24px;
      //    margin: 0;
      //    /* Text/type0 */
      //    background: $text-type-0;
      //    border-radius: 5px;
      //    &:checked {
      //      /* BG/type1 */
      //      background: $bg-type-1;
      //    }
      //  }
      //  label {
      //    margin-left: 12px;
      //    font-size: 14px;
      //    line-height: 17px;
      //    /* White */
      //    color: $text-type-0;
      //    mix-blend-mode: normal;
      //    opacity: 0.9;
      //  }
      //}
    }
  }
}
@keyframes bounce {
  50% {
    transform: scale(1.2);
  }
  75% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
  }
}
</style>
